import { ActionIcon, Box, Text } from '@mantine/core';
import TrashIcon from '@/icons/trash.svg';
import { useMutation } from '@tanstack/react-query';
import { useConfirmationModal } from '../../../components/modal/ConfirmationModalContext';
import { deletePublishedModel } from '../../actions/deletePublishedModel';
import { showErrorNotification, showSuccessNotification } from '../../../components/shared/notifications';
import { usePublishedOptimisticUi } from '../../hooks/usePublishedOptimisticUi';
import { getImpersonation } from '@/utils/impersonation';

type DeleteProps = {
  id: number;
  variant?: 'small' | 'normal';
  assetId: number;
};

export const Delete = ({ id, variant = 'normal', assetId }: DeleteProps) => {
  const { showConfirmationModal } = useConfirmationModal();
  const { deletePublishedModel: optimisiticDelete, invalidateQueries } = usePublishedOptimisticUi();

  const { mutate: deleteModel } = useMutation({
    mutationFn: async () => {
      const { isImpersonating } = getImpersonation();
      if (isImpersonating) {
        throw new Error('IMPERSONATION');
      }

      const confirmed = await showConfirmationModal({
        action: 'Delete',
        content: <Text>Do you really want to delete this published embed? This action cannot be undone.</Text>,
        title: 'Confirm',
      });

      if (!confirmed) {
        return { type: 'cancel' };
      }

      return await deletePublishedModel(id);
    },

    onSuccess: (response) => {
      if (response?.type === 'cancel') {
        return;
      }
      optimisiticDelete(id, assetId);

      showSuccessNotification({
        title: 'Model deleted',
        message: 'Model has been successfully deleted ✅',
      });
      invalidateQueries(assetId);
    },
    onError: (error) => {
      if (error.message === 'IMPERSONATION') {
        showErrorNotification({
          title: 'Not allowed',
          message: 'Cannot delete model while impersonating user ❌',
        });
        return;
      }
      showErrorNotification({
        title: 'Error',
        message: 'Failed to delete model ❌',
      });
    },
  });

  if (variant === 'small') {
    return (
      <ActionIcon variant='transparent' color='primary' onClick={() => deleteModel()}>
        <TrashIcon style={{ width: 20, height: 20 }} />
      </ActionIcon>
    );
  }

  return (
    <Box h='100%' pt='xs' pr='md'>
      <ActionIcon
        variant='light'
        c='teal.0'
        style={{
          borderRadius: '50%',
        }}
        bg='teal.0'
        w={36}
        h={36}
        onClick={() => deleteModel()}
      >
        <TrashIcon style={{ width: 24, height: 24, color: 'black' }} />
      </ActionIcon>
    </Box>
  );
};
